import React, { memo } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircle from '@mui/icons-material/CheckCircle';
import WarningAmber from '@mui/icons-material/WarningAmber';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { StaffMember } from './types';
import { STAFF_PRIMARY, statusChipSx } from './staffStyles';

interface StaffDetailHeaderProps {
  member: StaffMember;
  /** Без права на блокировку статус показывается чипом */
  canBlock: boolean;
  /** Почему разблокировать нельзя (точка заблокирована, лимит занят); пусто - можно */
  unblockBlockedReason: string;
  canImpersonate: boolean;
  statusSaving: boolean;
  impersonating: boolean;
  onToggleStatus: () => void;
  onImpersonate: () => void;
  onClose: () => void;
}

/** Шапка карточки сотрудника - как у карточки «Пользователей»: закрытие, имя, статус и вход под сотрудником */
const StaffDetailHeader: React.FC<StaffDetailHeaderProps> = ({
  member,
  canBlock,
  unblockBlockedReason,
  canImpersonate,
  statusSaving,
  impersonating,
  onToggleStatus,
  onImpersonate,
  onClose
}) => {
  const statusLocked = !member.active && Boolean(unblockBlockedReason);

  return (
    <Box
      sx={{
        px: { xs: 2.5, sm: 3.5 },
        py: 2.2,
        bgcolor: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, minWidth: 0 }}>
        <IconButton
          onClick={onClose}
          aria-label="Закрыть карточку"
          sx={{
            bgcolor: '#eff6ff',
            color: STAFF_PRIMARY,
            border: '1px solid #bfdbfe',
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: '50%',
            '&:hover': { bgcolor: '#dbeafe' }
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ color: '#0f172a', fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.35rem' }, lineHeight: 1.2, wordBreak: 'break-word' }}
          >
            {member.fullName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
            Логин: <strong style={{ color: '#1e293b' }}>{member.login}</strong> • {member.kindTitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {canBlock ? (
          <Tooltip title={statusLocked ? unblockBlockedReason : member.active ? 'Заблокировать сотрудника' : 'Разблокировать сотрудника'} arrow>
            <span>
              <Button
                size="small"
                onClick={onToggleStatus}
                disabled={statusSaving || statusLocked}
                startIcon={member.active ? <CheckCircle sx={{ fontSize: 16 }} /> : <WarningAmber sx={{ fontSize: 16 }} />}
                sx={{
                  border: '1px solid',
                  borderColor: member.active ? '#a7f3d0' : '#fed7aa',
                  bgcolor: member.active ? '#ecfdf5' : '#fff7ed',
                  color: member.active ? '#059669' : '#c2410c',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  px: 1.6,
                  height: 34,
                  borderRadius: '8px',
                  textTransform: 'none',
                  opacity: statusSaving || statusLocked ? 0.65 : 1,
                  '&:hover': { bgcolor: member.active ? '#d1fae5' : '#ffedd5' },
                  '&.Mui-disabled': { color: member.active ? '#059669' : '#c2410c' }
                }}
              >
                {member.active ? 'Активен' : 'Заблокирован'}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Chip label={member.active ? 'Активен' : 'Заблокирован'} size="small" sx={statusChipSx(member.active)} />
        )}

        {canImpersonate && (
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
            onClick={onImpersonate}
            disabled={impersonating}
            sx={{
              bgcolor: '#4f46e5',
              color: '#ffffff',
              fontSize: '0.78rem',
              fontWeight: 700,
              px: 1.8,
              height: 34,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)',
              '&:hover': { bgcolor: '#4338ca' }
            }}
          >
            {impersonating ? 'Вход...' : 'Войти под сотрудником'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default memo(StaffDetailHeader);
